import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMyStartup, getStartupContacts, getStartupRadar, toggleTrophyParticipation, recordNdaDownload, getNdaStatus, uploadNdaFile, getNdaTemplateUrl } from '../../api';
import { ScoreGauge, RiskBadge } from '../../components/ScoreGauge';
import RadarChart from '../../components/RadarChart';
import { useToast } from '../../components/Toast';

const STATUS_LABELS = { brouillon: 'Brouillon', en_attente: 'En attente', actif: 'Actif', suspendu: 'Suspendu', archive: 'Archivé' };
const STATUS_COLORS = { brouillon: '#95A5A6', en_attente: '#F39C12', actif: '#27AE60', suspendu: '#E67E22', archive: '#E74C3C' };

const ELIGIBILITY_CRITERIA = [
  "Être une startup créée il y a moins de 5 ans",
  "Réaliser un CA entre 0,5M et 5M \u20ac",
  "Si levée de fonds prévue elle est comprise entre 0,5M et 10M \u20ac",
  "Avoir effectué une levée de fonds comprise entre 0,5 M€ et 10 M\u20ac",
  "Développer des produits et solutions contribuant aux grands enjeux contemporains, notamment en matière de souveraineté technologique, de transition écologique, de santé, de réindustrialisation et d\u2019accès aux services essentiels",
  "Ne pas délivrer uniquement des prestations intellectuelles à destination de l\u2019écosystème (les ESN spécialisées dans le secteur financier ne sont pas éligibles)",
  "Avoir son siège social en France",
  "Avoir sa R&D en France",
  "Ne pas avoir son capital majoritairement détenu par une autre entreprise, grand groupe ou institution",
  "Ne pas avoir son capital majoritairement détenu par une société étrangère",
];

// Critères financiers en règle OU (assouplissement 2026) : le candidat est
// éligible dès qu'AU MOINS UNE de ces conditions financières est remplie
// (CA en range OU levée en range), au lieu de toutes les exiger cumulativement.
// Les autres critères restent obligatoires (ET). Indices dans ELIGIBILITY_CRITERIA.
const FINANCIAL_OR_GROUP = [1, 2, 3];
const NON_FINANCIAL_IDX = ELIGIBILITY_CRITERIA
  .map((_, i) => i)
  .filter(i => !FINANCIAL_OR_GROUP.includes(i));

const TROPHY_TIMELINE = [
  { label: 'Appel à candidature', date: '11 mai 2026', icon: '📢' },
  { label: 'Clôture des candidatures', date: '11 septembre 2026', icon: '📋' },
  { label: 'Audition des finalistes', date: '14 octobre 2026', icon: '🎤' },
  { label: 'Cérémonie 2026 — Élysées Biarritz, Paris 8e', date: '18 novembre 2026', icon: '🏆' },
];

export default function StartupDashboard() {
  const toast = useToast();
  const [startup, setStartup] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [radar, setRadar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [ndaDownloaded, setNdaDownloaded] = useState(false);
  const [ndaUploaded, setNdaUploaded] = useState(false);
  const [ndaFileName, setNdaFileName] = useState('');
  const [ndaSigned, setNdaSigned] = useState(false);
  const [ndaStatus, setNdaStatus] = useState(null);
  const [ndaValidatedAt, setNdaValidatedAt] = useState(null);
  const [ndaRejected, setNdaRejected] = useState(false);
  const [ndaRejectionReason, setNdaRejectionReason] = useState('');
  const [uploading, setUploading] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(null); // null = auto-detect on load
  const [calendarOpen, setCalendarOpen] = useState(true);
  const [ndaSectionOpen, setNdaSectionOpen] = useState(true);
  const [criteriaOpen, setCriteriaOpen] = useState(true);
  const [checkedCriteria, setCheckedCriteria] = useState(() => {
    try { return JSON.parse(localStorage.getItem('isi_criteria_checked') || '[]'); } catch { return []; }
  });
  // Règle OU : au moins UNE condition financière (CA ou levée) + TOUS les
  // critères non-financiers. eligRequiredTotal compte le bloc financier comme
  // un seul « point » à satisfaire (d'où NON_FINANCIAL_IDX.length + 1).
  const financialSatisfied = FINANCIAL_OR_GROUP.some(i => checkedCriteria.includes(i));
  const nonFinancialChecked = NON_FINANCIAL_IDX.filter(i => checkedCriteria.includes(i)).length;
  const allCriteriaChecked = nonFinancialChecked === NON_FINANCIAL_IDX.length && financialSatisfied;
  const eligRequiredMet = nonFinancialChecked + (financialSatisfied ? 1 : 0);
  const eligRequiredTotal = NON_FINANCIAL_IDX.length + 1;
  const toggleCriterion = (idx) => {
    const next = checkedCriteria.includes(idx) ? checkedCriteria.filter(i => i !== idx) : [...checkedCriteria, idx];
    setCheckedCriteria(next);
    localStorage.setItem('isi_criteria_checked', JSON.stringify(next));
  };
  const renderCriterion = (idx) => {
    const criterion = ELIGIBILITY_CRITERIA[idx];
    const checked = checkedCriteria.includes(idx);
    return (
      <label key={idx} onClick={() => toggleCriterion(idx)}
        style={{
          display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer',
          padding: '10px 14px', borderRadius: 8,
          border: `1px solid ${checked ? '#A5D6A7' : '#E0E0E0'}`,
          background: checked ? '#F1F8E9' : 'white',
          transition: 'all 0.15s'
        }}>
        <div style={{
          width: 22, height: 22, borderRadius: 4, flexShrink: 0, marginTop: 1,
          border: `2px solid ${checked ? '#43A047' : '#BDBDBD'}`,
          background: checked ? '#43A047' : 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 14, fontWeight: 700,
          transition: 'all 0.15s'
        }}>
          {checked && '✓'}
        </div>
        <span style={{ fontSize: 13, lineHeight: 1.5, color: checked ? '#2E7D32' : 'var(--text)' }}>
          {criterion}
        </span>
      </label>
    );
  };

  const loadNdaStatus = () => {
    getNdaStatus().then(nda => {
      setNdaStatus(nda.status);
      setNdaDownloaded(nda.downloaded);
      setNdaUploaded(nda.uploaded);
      setNdaFileName(nda.filename || '');
      setNdaSigned(nda.validated);
      setNdaValidatedAt(nda.validated_at);
      setNdaRejected(nda.rejected);
      setNdaRejectionReason(nda.rejection_reason || '');
      // Auto-collapse if NDA uploaded and waiting for validation (complete dossier)
      setRegistrationOpen(prev => prev !== null ? prev : nda.status !== 'uploaded');
    }).catch(() => {
      setRegistrationOpen(prev => prev !== null ? prev : true);
    });
  };

  const load = () => {
    getMyStartup().then(s => {
      setStartup(s);
      if (s.note_sectorielle) getStartupRadar(s.id).then(setRadar).catch(() => {});
      getStartupContacts(s.id).then(setContacts).catch(() => {});
    }).catch(console.error).finally(() => setLoading(false));
    loadNdaStatus();
  };

  useEffect(() => { load(); }, []);

  const handleTrophyToggle = async (participate) => {
    // Withdrawing AFTER having submitted has stronger consequences (clears
    // the submission flag, reopens the QIT and the Data Room mandatory
    // documents). Make sure the user is aware before we let them through.
    if (!participate && startup) {
      const activeYear = startup.active_campaign_year;
      const submittedStr = startup.qit_submitted_years || '';
      const submittedYears = submittedStr
        .split(',')
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !Number.isNaN(n));
      const wasSubmitted = activeYear && submittedYears.includes(activeYear);
      if (wasSubmitted) {
        const ok = await toast.confirm(
          `Vous vous apprêtez à retirer votre candidature soumise pour les Trophées ${activeYear}.\n\n`
          + `• L'information « candidature soumise » sera effacée.\n`
          + `• Le questionnaire QIT redeviendra modifiable.\n`
          + `• Les documents obligatoires de la Data Room redeviendront modifiables.\n`
          + `• Toute resoumission devra repasser par la clause de certification.\n\n`
          + `Confirmer le retrait ?`
        );
        if (!ok) return;
      }
    }
    setToggling(true);
    try {
      await toggleTrophyParticipation(participate);
      if (!participate) {
        localStorage.removeItem('isi_criteria_checked');
        setNdaSigned(false);
        setNdaDownloaded(false);
        setNdaUploaded(false);
        setNdaFileName('');
        setNdaStatus(null);
        setNdaRejected(false);
        setNdaRejectionReason('');
        setCheckedCriteria([]);
      }
      load();
    } catch (err) { toast.error(err.message); }
    setToggling(false);
  };

  if (loading) return <div className="loading">Chargement...</div>;
  if (!startup) return (
    <div style={{ padding: 60, textAlign: 'center' }}>
      <h2>Bienvenue sur la plateforme ISI</h2>
      <p style={{ color: 'var(--text-light)', marginTop: 12 }}>Votre profil startup sera bientôt disponible.</p>
    </div>
  );

  const isTrophyParticipant = startup.trophy_participant;

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <h1 style={{ marginBottom: 8 }}>Tableau de bord - {startup.name}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 24 }}>
          <span className="badge" style={{ background: `${STATUS_COLORS[startup.status] || '#95A5A6'}20`, color: STATUS_COLORS[startup.status] || '#95A5A6' }}>
            {STATUS_LABELS[startup.status] || startup.status}
          </span>
          {startup.risk_level && <RiskBadge level={startup.risk_level} />}
          {startup.categorie && <span className="badge badge-secteur">{startup.categorie}</span>}
          {isTrophyParticipant && <span className="badge" style={{ background: '#FFF8E1', color: '#F57F17' }}>Participante aux Trophées</span>}
        </div>

        {/* ========== ÉTAT 1 : Non participant — bloc "Trophées de l'Innovation" ========== */}
        {!isTrophyParticipant && (
          <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16 }}>Trophées de l'Innovation</h3>
                <p style={{ color: 'var(--text-light)', fontSize: 13, marginTop: 6, marginBottom: 0 }}>
                  Souhaitez-vous participer aux Trophées de l'Innovation ? En participant et après avoir rempli le questionnaire ISI, vous pourrez obtenir le score ISI et ensuite être évalué par le jury.
                </p>
              </div>
              <div style={{ flexShrink: 0, marginLeft: 16 }}>
                <button onClick={() => handleTrophyToggle(true)} className="btn btn-accent"
                  disabled={toggling} style={{ fontSize: 14, padding: '12px 24px' }}>
                  {toggling ? '...' : 'Participer aux Trophées'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========== ÉTAT 2 : Participant, NDA non signé — bloc "Enregistrement de ma candidature" ========== */}
        {isTrophyParticipant && !ndaSigned && (
          <div className="card" style={{ marginBottom: 24, border: '2px solid #2E75B6', background: '#F5F9FF' }}>
            <div onClick={() => setRegistrationOpen(!registrationOpen)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: registrationOpen ? 20 : 0 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#2E75B6' }}>
                Enregistrement de ma candidature
              </h3>
              <span style={{ fontSize: 20, color: '#2E75B6', transition: 'transform 0.2s', transform: registrationOpen ? 'rotate(180deg)' : 'rotate(0)' }}>
                ▼
              </span>
            </div>

            {/* Message résumé quand le bloc est réduit et NDA en attente de validation */}
            {!registrationOpen && ndaUploaded && !ndaRejected && ndaStatus === 'uploaded' && (
              <div style={{ marginTop: 12, padding: '12px 16px', background: '#E8F5E9', borderRadius: 8, fontSize: 13, color: '#2E7D32', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>✅</span>
                Votre dossier est complet. Votre NDA signé est en attente de validation par l'administrateur.
              </div>
            )}

            {registrationOpen && <>
            {/* Calendrier des Trophées — collapsible */}
            <div style={{ marginBottom: 28 }}>
              <div onClick={() => setCalendarOpen(!calendarOpen)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: calendarOpen ? 16 : 0 }}>
                <h4 style={{ fontSize: 15, color: '#2E75B6', margin: 0 }}>
                  Calendrier des Trophées de l'Innovation
                </h4>
                <span style={{ fontSize: 20, color: '#2E75B6', transition: 'transform 0.2s', transform: calendarOpen ? 'rotate(180deg)' : 'rotate(0)' }}>
                  ▼
                </span>
              </div>
              {calendarOpen && (
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '0 10px' }}>
                  <div style={{ position: 'absolute', left: 30, right: 30, top: 20, height: 3, background: '#2E75B6', opacity: 0.25, borderRadius: 2 }} />
                  {TROPHY_TIMELINE.map((step, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative', zIndex: 1 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: i === 0 ? '#2E75B6' : 'white',
                        border: '3px solid #2E75B6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 18, marginBottom: 10, boxShadow: '0 2px 6px rgba(46,117,182,0.2)'
                      }}>
                        {step.icon}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', textAlign: 'center', lineHeight: 1.3 }}>{step.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4, textAlign: 'center' }}>{step.date}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Accord de confidentialité (NDA) — collapsible */}
            <div style={{ borderTop: '1px solid #D0E3F5', paddingTop: 20, marginBottom: 28 }}>
              <div onClick={() => setNdaSectionOpen(!ndaSectionOpen)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: ndaSectionOpen ? 16 : 0 }}>
                <h4 style={{ fontSize: 15, color: '#2E75B6', margin: 0 }}>
                  Accord de confidentialité (NDA)
                </h4>
                <span style={{ fontSize: 20, color: '#2E75B6', transition: 'transform 0.2s', transform: ndaSectionOpen ? 'rotate(180deg)' : 'rotate(0)' }}>
                  ▼
                </span>
              </div>
              {ndaSectionOpen && (
                <>
                  <p style={{ fontSize: 14, lineHeight: 1.8, color: 'var(--text)', marginBottom: 8 }}>
                    Dans le cadre de votre participation aux Trophées de l'Innovation, vous allez être amené(e) à communiquer des informations
                    confidentielles sur votre entreprise (données financières, stratégie, propriété intellectuelle, etc.).
                  </p>
                  <p style={{ fontSize: 14, lineHeight: 1.8, color: 'var(--text)', marginBottom: 20 }}>
                    Afin de protéger ces informations, nous vous demandons de signer un <strong>Accord de Non-Divulgation (NDA)</strong>. Ce NDA garantit que toutes les données transmises resteront strictement confidentielles
                    et ne seront utilisées que dans le cadre de l'évaluation par le jury.
                  </p>

                  {/* Bouton Télécharger le NDA */}
                  <div style={{ marginBottom: 20, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={async () => {
                      try {
                        await recordNdaDownload();
                        setNdaDownloaded(true);
                        // Télécharger le template NDA réel
                        const token = localStorage.getItem('isi_token');
                        const res = await fetch(getNdaTemplateUrl(), { headers: { 'Authorization': `Bearer ${token}` } });
                        if (!res.ok) throw new Error('Erreur téléchargement');
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        const disposition = res.headers.get('Content-Disposition') || '';
                        const match = disposition.match(/filename="?([^"]+)"?/);
                        a.href = url;
                        a.download = match ? decodeURIComponent(match[1]) : 'NDA_Trophees_Innovation_2026.docx';
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch (e) { console.error(e); toast.error('Erreur lors du téléchargement du NDA'); }
                    }} className="btn btn-primary" style={{ padding: '12px 28px', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                      📄 Télécharger le NDA
                    </button>
                    {ndaDownloaded && (
                      <span style={{ fontSize: 13, color: '#2E7D32', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                        ✓ NDA téléchargé
                      </span>
                    )}
                  </div>

                  {/* NDA rejeté — message d'alerte */}
                  {ndaRejected && (
                    <div style={{ padding: '12px 18px', background: '#FFEBEE', borderRadius: 8, fontSize: 13, color: '#C62828', marginBottom: 16, lineHeight: 1.6 }}>
                      <strong>Votre NDA a été rejeté par l'administrateur.</strong>
                      {ndaRejectionReason && <> Motif : {ndaRejectionReason}</>}
                      <br />Veuillez déposer un nouveau NDA signé conforme.
                    </div>
                  )}

                  {/* Zone d'import du NDA signé */}
                  <div style={{ padding: 20, border: '2px dashed #90CAF9', borderRadius: 10, background: '#FAFCFF', textAlign: 'center' }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#2E75B6', marginBottom: 8 }}>
                      Déposez votre NDA signé
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 14 }}>
                      Formats acceptés : PDF, JPG, PNG — Taille max : 10 Mo
                    </p>
                    {!ndaUploaded || ndaRejected ? (
                      <>
                        <input type="file" id="nda-upload" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setUploading(true);
                              try {
                                await uploadNdaFile(file);
                                setNdaUploaded(true);
                                setNdaFileName(file.name);
                                setNdaRejected(false);
                                setNdaRejectionReason('');
                              } catch (err) { toast.error('Erreur lors du dépôt : ' + err.message); }
                              setUploading(false);
                            }
                          }} />
                        <button onClick={() => document.getElementById('nda-upload').click()}
                          disabled={uploading}
                          className="btn btn-outline" style={{ fontSize: 13, padding: '10px 24px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {uploading ? 'Envoi en cours...' : '📎 Choisir un fichier'}
                        </button>
                      </>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                        <span style={{ fontSize: 13, color: '#2E7D32', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                          ✓ {ndaFileName}
                        </span>
                        <span className="badge" style={{ background: '#FFF3E0', color: '#E65100', fontSize: 10 }}>
                          En attente de validation
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Critères d'éligibilité */}
            <div style={{ marginTop: 28, borderTop: '1px solid #D0E3F5', paddingTop: 20 }}>
              <div onClick={() => setCriteriaOpen(!criteriaOpen)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: criteriaOpen ? 16 : 0 }}>
                <h4 style={{ fontSize: 15, color: '#2E75B6', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  Critères d'éligibilité
                  <span className="badge" style={{
                    background: allCriteriaChecked ? '#E8F5E9' : '#FFF3E0',
                    color: allCriteriaChecked ? '#2E7D32' : '#E65100', fontSize: 11
                  }}>
                    {eligRequiredMet}/{eligRequiredTotal}
                  </span>
                </h4>
                <span style={{ fontSize: 20, color: '#2E75B6', transition: 'transform 0.2s', transform: criteriaOpen ? 'rotate(180deg)' : 'rotate(0)' }}>
                  ▼
                </span>
              </div>
              {criteriaOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <p style={{ fontSize: 13, color: 'var(--text-light)', fontStyle: 'italic', margin: '0 0 6px 0' }}>
                    Veuillez vérifier votre éligibilité en remplissant le questionnaire ci-dessous
                  </p>
                  {NON_FINANCIAL_IDX.filter(i => i < FINANCIAL_OR_GROUP[0]).map(renderCriterion)}
                  {/* Bloc financier — règle OU : au moins une condition suffit */}
                  <div style={{
                    border: `1px solid ${financialSatisfied ? '#A5D6A7' : '#FFE082'}`,
                    borderRadius: 10, padding: '12px 14px',
                    background: financialSatisfied ? '#F6FBF2' : '#FFFDF5',
                    display: 'flex', flexDirection: 'column', gap: 8
                  }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: '#2E75B6' }}>
                      Situation financière — au moins <u>une</u> des conditions suivantes suffit (règle OU)
                    </div>
                    {FINANCIAL_OR_GROUP.map(renderCriterion)}
                    <div style={{ fontSize: 12, fontStyle: 'italic', color: financialSatisfied ? '#2E7D32' : '#E65100' }}>
                      {financialSatisfied ? '✓ Condition financière remplie' : 'Cochez au moins une de ces conditions'}
                    </div>
                  </div>
                  {NON_FINANCIAL_IDX.filter(i => i > FINANCIAL_OR_GROUP[FINANCIAL_OR_GROUP.length - 1]).map(renderCriterion)}
                </div>
              )}
            </div>

            {/* Statut de complétude du dossier */}
            <div style={{ marginTop: 24, borderTop: '1px solid #D0E3F5', paddingTop: 20 }}>
              <h4 style={{ fontSize: 15, color: '#2E75B6', margin: '0 0 16px 0' }}>
                Statut de votre dossier de candidature
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {/* NDA téléchargé */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: ndaDownloaded ? '#F1F8E9' : '#FFF8E1', border: `1px solid ${ndaDownloaded ? '#A5D6A7' : '#FFE082'}` }}>
                  <span style={{ fontSize: 18 }}>{ndaDownloaded ? '✅' : '⬜'}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: ndaDownloaded ? '#2E7D32' : '#F57F17' }}>
                    NDA téléchargé
                  </span>
                </div>
                {/* NDA signé déposé */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
                  background: ndaRejected ? '#FFEBEE' : ndaUploaded ? '#F1F8E9' : '#FFF8E1',
                  border: `1px solid ${ndaRejected ? '#EF9A9A' : ndaUploaded ? '#A5D6A7' : '#FFE082'}` }}>
                  <span style={{ fontSize: 18 }}>{ndaRejected ? '❌' : ndaUploaded ? '✅' : '⬜'}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: ndaRejected ? '#C62828' : ndaUploaded ? '#2E7D32' : '#F57F17' }}>
                    NDA signé déposé
                    {ndaUploaded && !ndaRejected && <span style={{ fontWeight: 400, fontSize: 12 }}> (en attente de validation par l'administrateur)</span>}
                    {ndaRejected && <span style={{ fontWeight: 400, fontSize: 12 }}> (rejeté — veuillez redéposer)</span>}
                  </span>
                </div>
                {/* NDA validé */}
                {ndaValidatedAt && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#F1F8E9', border: '1px solid #A5D6A7' }}>
                    <span style={{ fontSize: 18 }}>✅</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#2E7D32' }}>
                      NDA validé par l'administrateur le {new Date(ndaValidatedAt).toLocaleDateString('fr-FR')} à {new Date(ndaValidatedAt).toLocaleTimeString('fr-FR')}
                    </span>
                  </div>
                )}
                {/* Critères d'éligibilité */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: allCriteriaChecked ? '#F1F8E9' : '#FFF8E1', border: `1px solid ${allCriteriaChecked ? '#A5D6A7' : '#FFE082'}` }}>
                  <span style={{ fontSize: 18 }}>{allCriteriaChecked ? '✅' : '⬜'}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: allCriteriaChecked ? '#2E7D32' : '#F57F17' }}>
                    Critères d'éligibilité remplis à {Math.round((eligRequiredMet / eligRequiredTotal) * 100)}%
                  </span>
                </div>
              </div>

              {/* Message de guidage */}
              {(() => {
                const missing = [];
                if (!ndaDownloaded) missing.push('télécharger le NDA');
                if (!ndaUploaded || ndaRejected) missing.push('déposer votre NDA signé');
                if (!allCriteriaChecked) missing.push('compléter les critères d\'éligibilité');
                if (missing.length === 0 && ndaStatus === 'uploaded') {
                  return (
                    <div style={{ padding: '14px 18px', background: '#E8F5E9', borderRadius: 8, fontSize: 13, color: '#2E7D32', lineHeight: 1.6 }}>
                      Votre dossier est complet. Votre NDA signé est en attente de validation par l'administrateur.
                    </div>
                  );
                }
                if (missing.length === 0 && ndaStatus === 'validated') {
                  return (
                    <div style={{ padding: '14px 18px', background: '#E8F5E9', borderRadius: 8, fontSize: 13, color: '#2E7D32', lineHeight: 1.6 }}>
                      <strong>Votre NDA a été validé.</strong> Vous pouvez accéder au questionnaire QIT.
                    </div>
                  );
                }
                if (missing.length > 0) {
                  return (
                    <div style={{ padding: '14px 18px', background: '#FFF8E1', borderRadius: 8, fontSize: 13, color: '#795548', lineHeight: 1.6 }}>
                      Pour finaliser votre candidature, vous devez encore : <strong>{missing.join(', ')}</strong>.
                      {ndaUploaded && !ndaRejected && !allCriteriaChecked && ' Votre NDA déposé sera approuvé par l\'administrateur.'}
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Bouton Retirer ma candidature */}
            <div style={{ marginTop: 24, borderTop: '1px solid #D0E3F5', paddingTop: 20, textAlign: 'center' }}>
              <button onClick={() => handleTrophyToggle(false)} className="btn btn-outline"
                disabled={toggling} style={{ fontSize: 13, color: '#E74C3C', borderColor: '#E74C3C', padding: '12px 28px' }}>
                {toggling ? '...' : 'Retirer ma candidature'}
              </button>
            </div>
            </>}
          </div>
        )}

        {/* ========== ÉTAT 3 : Participant, NDA signé — bloc "Vous participez" ========== */}
        {isTrophyParticipant && ndaSigned && (
          <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid #F57F17', background: '#FFFDE7' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16 }}>Vous participez aux Trophées de l'Innovation</h3>
                <p style={{ color: 'var(--text-light)', fontSize: 13, marginTop: 6, marginBottom: 0 }}>
                  Votre startup est inscrite. Complétez le questionnaire QIT pour optimiser votre score ISI et votre classement.
                </p>
              </div>
              <div style={{ flexShrink: 0, marginLeft: 16 }}>
                <button onClick={() => handleTrophyToggle(false)} className="btn btn-outline"
                  disabled={toggling} style={{ fontSize: 12, color: '#E74C3C', borderColor: '#E74C3C' }}>
                  {toggling ? '...' : 'Se retirer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Présentation */}
        {startup.pitch && (
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginBottom: 8 }}>Présentation</h3>
            <p style={{ lineHeight: 1.8, color: 'var(--text-light)', fontSize: 14, margin: 0 }}>{startup.pitch}</p>
          </div>
        )}

        {/* Progress cards */}
        <div className="stats-grid" style={{ marginBottom: 32 }}>
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--primary-light)' }}>{Math.max(0, Math.min(100, startup.qit_progress || 0))}%</div>
            <div className="stat-label">Questionnaire QIT</div>
            {isTrophyParticipant && ndaSigned ? (
              <Link to="/startup/qit" className="btn btn-primary" style={{ marginTop: 12, fontSize: 12, padding: '6px 12px' }}>
                {startup.qit_progress > 0 ? 'Continuer' : 'Commencer'}
              </Link>
            ) : (
              <span style={{ marginTop: 12, fontSize: 11, color: 'var(--text-light)', display: 'block' }}>
                {isTrophyParticipant ? 'Complétez votre dossier pour accéder' : 'Participez aux Trophées'}
              </span>
            )}
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#E67E22' }}>
              {startup.taux_coherence ? `${parseFloat(startup.taux_coherence).toFixed(0)}%` : '-'}
            </div>
            <div className="stat-label">Taux de cohérence</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ fontSize: 20 }}>{startup.ca || '-'}</div>
            <div className="stat-label">Chiffre d'affaires</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ fontSize: 20 }}>Round {startup.round_levee || '-'}</div>
            <div className="stat-label">Levée de fonds</div>
          </div>
        </div>

        {/* Score ISI + Radar — visibles uniquement si un score existe ET l admin l a publié */}
        {startup.note_sectorielle && startup.score_published && (
          <div className="detail-grid">
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Mon Score ISI</h3>
                {startup.score_year && (
                  <span className="badge" style={{ background: '#E3F2FD', color: '#1565C0', fontSize: 11 }}>
                    Campagne {startup.score_year}
                  </span>
                )}
              </div>
              <div style={{ textAlign: 'center' }}>
                <ScoreGauge score={startup.note_sectorielle} size="lg" />
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 14, color: 'var(--text-light)' }}>Note Pivot: <strong>{parseFloat(startup.note_pivot).toFixed(2)}</strong></div>
                  <div style={{ fontSize: 14, color: 'var(--text-light)', marginTop: 4 }}>Note Sectorielle: <strong>{parseFloat(startup.note_sectorielle).toFixed(2)}</strong></div>
                </div>
                {startup.top_force && <div style={{ marginTop: 12, fontSize: 13, color: '#27AE60' }}>Top Force: {startup.top_force}</div>}
                {startup.top_faiblesse && <div style={{ fontSize: 13, color: '#E74C3C', marginTop: 4 }}>Top Faiblesse: {startup.top_faiblesse}</div>}
                {startup.score_hidden_current_year && (
                  <div style={{ marginTop: 16, padding: '10px 16px', background: '#FFF8E1', borderRadius: 8, fontSize: 12, color: '#F57F17' }}>
                    Le score {startup.score_hidden_current_year} sera visible après la clôture des Trophées.
                    {startup.score_year && <span> Score affiché : campagne {startup.score_year}.</span>}
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <h3 style={{ marginBottom: 16 }}>Radar de Résilience</h3>
              {radar ? (
                <RadarChart labels={radar.labels} startupData={radar.startup} sectorAvg={radar.sector_avg} startupName={startup.name} height={350} />
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-light)' }}>
                  Disponible après le calcul du score ISI
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact requests received */}
        {contacts.length > 0 && (
          <div className="card" style={{ marginTop: 32 }}>
            <h3 style={{ marginBottom: 16 }}>Demandes de mise en relation ({contacts.length})</h3>
            {contacts.map(c => (
              <div key={c.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <strong>{c.first_name} {c.last_name}</strong> - {c.company}
                  <div style={{ fontSize: 13, color: 'var(--text-light)', marginTop: 4 }}>{c.message}</div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-light)' }}>{new Date(c.created_at).toLocaleDateString('fr-FR')}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
